import { useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import { ErrorBanner, Spinner } from '../components/common'
import type { VistaNotifiche } from '../components/NotificheCertificati'
import { NotificheCertificati } from '../components/NotificheCertificati'
import type { VistaCertificati } from '../components/RegistroCertificati'
import { RegistroCertificati } from '../components/RegistroCertificati'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { TrafficoInChiaro } from './PlaintextTraffic'

/**
 * Sicurezza → Cifratura: what travels or is offered without proper
 * encryption. Five tabs: the clear-text traffic between machines, the
 * register of the certificates the machines expose, their expiries, the
 * self-signed ones, and the notifications about them (rules and log,
 * `&vista=log`). The tab is in the URL (`?tab=`), so a link or the back
 * button lands on the same view. See plaintext_traffic_service,
 * tls_inventory_service and cert_alert_service.
 */
type Scheda = 'chiaro' | VistaCertificati | 'notifiche'

const SCHEDE: Array<{ key: Scheda; label: () => string }> = [
  { key: 'chiaro', label: () => t('Traffico in chiaro') },
  { key: 'anagrafica', label: () => t('Anagrafica dei certificati') },
  { key: 'scadenze', label: () => t('Scadenze e certificati scaduti') },
  { key: 'autofirmati', label: () => t('Certificati autofirmati') },
  { key: 'notifiche', label: () => t('Notifiche') },
]

export default function Cifratura({ predefinita = 'chiaro' }: {
  /** The tab without `?tab=`: /encryption/certificates opens the register. */
  predefinita?: Scheda
}) {
  const [params, setParams] = useSearchParams()
  // `notifiche=1` was the link to the notifications when they sat inside the register.
  const scheda = (params.get('notifiche') === '1' ? 'notifiche'
    : SCHEDE.find((s) => s.key === params.get('tab'))?.key ?? predefinita) as Scheda
  const vai = (s: Scheda) => setParams({ tab: s })
  const vistaNotifiche: VistaNotifiche = params.get('vista') === 'log' ? 'log' : 'regole'
  const apriNotifiche = (v: VistaNotifiche = 'regole') => setParams(v === 'log' ? { tab: 'notifiche', vista: 'log' } : { tab: 'notifiche' })
  const certificati = useAsync(() => (scheda === 'chiaro' ? Promise.resolve(null) : api.tlsCertificates()),
    [scheda === 'chiaro'])
  const n = (x: number) => x.toLocaleString(localeCorrente())
  const d = certificati.data

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Cifratura')}</h1>
          <p className="page__subtitle">
            {t('Individua le comunicazioni non cifrate tra gli asset e analizza i certificati TLS presentati dai loro servizi, indicando chi li espone, chi li ha emessi, quando scadono e quali sono autofirmati o utilizzano configurazioni deboli.')}
          </p>
        </div>
      </header>
      <nav className="tabs" role="tablist">
        {SCHEDE.map((s) => (
          <button key={s.key} type="button" role="tab" aria-selected={scheda === s.key}
            className={`tabs__tab${scheda === s.key ? ' tabs__tab--active' : ''}`} onClick={() => vai(s.key)}>
            {s.label()}
          </button>
        ))}
      </nav>

      {scheda === 'chiaro' ? <TrafficoInChiaro /> : (
        <>
          <ErrorBanner error={certificati.error} />
          {certificati.loading && !d ? <Spinner /> : d && scheda === 'notifiche' ? (
            <NotificheCertificati dati={d} vista={vistaNotifiche} onVista={apriNotifiche} />
          ) : d && (
            <>
              <div className="stats">
                <button type="button" className="stat" onClick={() => vai('anagrafica')} aria-pressed={scheda === 'anagrafica'}>
                  <div className="stat__value">{n(d.total)}</div>
                  <div className="stat__label">{t('Certificati su {h} host ({u} diversi)', { h: d.hosts, u: d.distinct })}</div>
                </button>
                <button type="button" className={`stat stat--${d.expired ? 'error' : 'ok'}`} onClick={() => vai('scadenze')}>
                  <div className="stat__value">{n(d.expired)}</div>
                  <div className="stat__label">{t('Scaduti')}</div>
                </button>
                <button type="button" className={`stat stat--${d.expiring ? 'warn' : 'ok'}`} onClick={() => vai('scadenze')}>
                  <div className="stat__value">{n(d.expiring)}</div>
                  <div className="stat__label">{t('Scadono entro 30 giorni')}</div>
                </button>
                <button type="button" className={`stat stat--${d.self_signed_exposed ? 'warn' : 'ok'}`} onClick={() => vai('autofirmati')}>
                  <div className="stat__value">{n(d.self_signed)}</div>
                  <div className="stat__label">{t('Autofirmati ({e} raggiungibili da altre macchine)', { e: d.self_signed_exposed })}</div>
                </button>
                <button type="button" className={`stat stat--${d.weak ? 'warn' : 'ok'}`} onClick={() => vai('anagrafica')}>
                  <div className="stat__value">{n(d.weak)}</div>
                  <div className="stat__label">{t('Con chiave, firma o protocollo deboli')}</div>
                </button>
              </div>
              {scheda !== 'anagrafica' && (
                <p className="field__hint" style={{ marginTop: 0 }}>
                  {scheda === 'scadenze'
                    ? t('Chi viene avvisato prima che scadano si decide nella scheda «Notifiche».')
                    : t('Chi viene avvisato quando ne compare uno nuovo si decide nella scheda «Notifiche».')}
                  {' '}
                  <button type="button" className="link" onClick={() => apriNotifiche()}>{t('Imposta le notifiche')}</button>
                </p>
              )}
              <RegistroCertificati dati={d} vista={scheda as VistaCertificati} />
            </>
          )}
        </>
      )}
    </>
  )
}
